class MainBlogArchives {
  constructor(root) {
    this.root = root;
    this.archiveContainer = root.querySelector('[data-amd-archives]');
    this.searchForm = root.querySelector('[data-blog-search-form]');
    this.searchInput = root.querySelector('[data-blog-search-input]');
    this.postsList = root.querySelector('[data-blog-posts-list]');
    this.cardsContainer = root.querySelector('[data-blog-cards]');
    this.postCards = this.cardsContainer ? Array.from(this.cardsContainer.querySelectorAll('[data-blog-post]')) : [];
    this.noResults = root.querySelector('[data-blog-no-results]');
    this.blogTitle = root.querySelector('[data-blog-title]');
    this.defaultTitle = this.blogTitle?.dataset.defaultTitle || 'Blog';
    this.baseBlogUrl = this.cardsContainer?.dataset.blogBaseUrl || '';
    this.totalPages = parseInt(this.cardsContainer?.dataset.blogTotalPages || '1', 10) || 1;
    this.archiveBuilt = false;
    this.loadingMessage = null;
    this.cacheLoadedUrls = new Set();
    this.isLoadingPages = false;

    this.onToggleClick = this.onToggleClick.bind(this);
    this.onSearchSubmit = this.onSearchSubmit.bind(this);
  }

  init() {
    if (!(this.archiveContainer instanceof HTMLElement)) {
      this.initSearchFilter();
      return;
    }

    this.archiveContainer.addEventListener('click', this.onToggleClick);
    this.initSearchFilter();
    this.observeArchive();
  }

  /**
   * Defer building the full archive until the widget is about to enter the
   * viewport. While the visitor is at the top of the page the archive does no
   * work and never grows, so nothing shifts the page under them.
   */
  observeArchive() {
    if (!(this.archiveContainer instanceof HTMLElement) || this.totalPages <= 1) {
      return;
    }
    if (!('IntersectionObserver' in window)) {
      this.buildFullArchive();
      return;
    }
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            obs.disconnect();
            this.buildFullArchive();
          }
        });
      },
      { rootMargin: '300px' }
    );
    observer.observe(this.archiveContainer);
  }

  /**
   * The Liquid archive only sees the current paginated page of `blog.articles`
   * (and Liquid caps that array anyway), so old years never appear. This walks
   * every page via fetch and rebuilds the year → month → posts tree from all of
   * them, covering the blog's full history.
   */
  async buildFullArchive() {
    const archiveList = this.archiveContainer?.querySelector('.amd-widget-archives-list');
    if (!(archiveList instanceof HTMLElement) || this.archiveBuilt) {
      return;
    }
    if (this.totalPages <= 1) {
      return;
    }

    this.archiveBuilt = true;
    // Keep the current years visible and show a subtle loader below them while
    // the full history loads in the background (deferred). The complete list is
    // swapped in at its natural height once ready — no fixed box, no scrollbar.
    const loader = document.createElement('p');
    loader.className = 'amd-widget-archives-loading';
    loader.textContent = 'Loading…';
    this.archiveContainer.appendChild(loader);

    const seen = new Set();
    // year (number) -> Map(monthNumber 1-12 -> [{ title, url }])
    const years = new Map();

    const collect = (cards) => {
      cards.forEach((card) => {
        const year = parseInt(card.dataset.publishedYear, 10);
        const month = parseInt(card.dataset.publishedMonth, 10);
        if (!year || !month) {
          return;
        }
        const link = card.querySelector('[data-blog-post-title]');
        const url = link?.getAttribute('href');
        const title = link?.textContent.trim();
        if (!url || seen.has(url)) {
          return;
        }
        seen.add(url);
        if (!years.has(year)) {
          years.set(year, new Map());
        }
        const months = years.get(year);
        if (!months.has(month)) {
          months.set(month, []);
        }
        months.get(month).push({ title, url });
      });
    };

    // Current page is already in the DOM.
    if (this.cardsContainer instanceof HTMLElement) {
      collect(Array.from(this.cardsContainer.querySelectorAll('[data-blog-post]')));
    }

    const base = this.baseBlogUrl || window.location.pathname.split('/page/')[0];
    const urls = [];
    for (let page = 1; page <= this.totalPages; page += 1) {
      urls.push(`${base}?page=${page}`);
    }

    const parser = new DOMParser();
    const queue = urls.slice();
    const worker = async () => {
      while (queue.length) {
        const url = queue.shift();
        try {
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) {
            continue;
          }
          const html = await response.text();
          const doc = parser.parseFromString(html, 'text/html');
          collect(Array.from(doc.querySelectorAll('[data-blog-post]')));
        } catch (error) {
          /* skip unreachable page */
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(5, urls.length) }, worker));

    // Render the complete archive in ONE shot, replacing the loading state — so
    // every year appears together, only once all pages have loaded.
    const fragment = document.createDocumentFragment();
    Array.from(years.keys())
      .sort((a, b) => b - a)
      .forEach((year) => {
        const months = years.get(year);
        const sortedMonths = Array.from(months.keys()).sort((a, b) => b - a);
        fragment.appendChild(this.createYearLi(year, sortedMonths, months));
      });
    // Remove the loader and swap the current years for the complete list in one
    // atomic operation, at natural height. Preserve the scroll position so the
    // browser's scroll-anchoring doesn't push the viewport down as the list grows.
    const scrollY = window.scrollY;
    loader.remove();
    archiveList.replaceChildren(fragment);
    window.scrollTo(0, scrollY);
    window.requestAnimationFrame(() => window.scrollTo(0, scrollY));
  }

  createYearLi(year, sortedMonths, months) {
    const li = document.createElement('li');
    li.className = 'amd-widget-archives-list-year';
    li.setAttribute('data-archive-year', String(year));

    const button = document.createElement('button');
    button.className = 'amd-widget-archives-list-year-title';
    button.type = 'button';
    button.setAttribute('data-archive-toggle', '');
    button.setAttribute('aria-expanded', 'false');
    button.innerHTML = '<span class="amd-widget-archives-more" aria-hidden="true"></span>';
    button.append(String(year));

    const ul = document.createElement('ul');
    ul.className = 'amd-widget-archives-list-months';
    ul.setAttribute('role', 'list');
    sortedMonths.forEach((month) => ul.appendChild(this.createMonthLi(month, months.get(month))));

    li.append(button, ul);
    return li;
  }

  createMonthLi(month, posts) {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December',
    ];
    const li = document.createElement('li');
    li.className = 'amd-widget-archives-list-month';
    li.setAttribute('data-archive-month', String(month));

    const button = document.createElement('button');
    button.className = 'amd-widget-archives-list-month-title';
    button.type = 'button';
    button.setAttribute('data-archive-toggle', '');
    button.setAttribute('aria-expanded', 'false');
    button.innerHTML = '<span class="amd-widget-archives-more" aria-hidden="true"></span>';
    button.append(monthNames[month - 1] || '');

    const ul = document.createElement('ul');
    ul.className = 'amd-widget-archives-list-posts';
    ul.setAttribute('role', 'list');
    posts.forEach((post) => ul.appendChild(this.createPostLi(post)));

    li.append(button, ul);
    return li;
  }

  createPostLi(post) {
    const li = document.createElement('li');
    li.className = 'amd-widget-archives-list-post';
    const anchor = document.createElement('a');
    anchor.href = post.url;
    anchor.textContent = post.title || '';
    li.appendChild(anchor);
    return li;
  }

  destroy() {
    if (!(this.archiveContainer instanceof HTMLElement)) {
      return;
    }

    this.archiveContainer.removeEventListener('click', this.onToggleClick);

    if (this.searchForm instanceof HTMLElement) {
      this.searchForm.removeEventListener('submit', this.onSearchSubmit);
    }
  }

  initSearchFilter() {
    if (!(this.searchForm instanceof HTMLElement) || !(this.searchInput instanceof HTMLInputElement)) {
      return;
    }

    this.searchForm.addEventListener('submit', this.onSearchSubmit);
  }

  onSearchSubmit(event) {
    event.preventDefault();
    if (!(this.searchInput instanceof HTMLInputElement)) {
      return;
    }
    const term = this.searchInput.value.trim();

    if (!term) {
      this.resetToFirstPage();
      return;
    }

    this.ensureAllPagesLoaded().then(() => {
      this.applySearchFilter(term);
    });
  }

  applySearchFilter(rawTerm) {
    const term = (rawTerm || '').trim().toLowerCase();

    let visibleCount = 0;
    this.postCards.forEach((card) => {
      const titleEl = card.querySelector('[data-blog-post-title]');
      const excerptEl = card.querySelector('[data-blog-post-excerpt]');
      const metaEl = card.querySelector('[data-blog-post-meta]');

      const haystack = [titleEl?.textContent, excerptEl?.textContent, metaEl?.textContent].join(' ').toLowerCase();
      const matches = term === '' || haystack.includes(term);

      if (matches) {
        card.removeAttribute('data-blog-filter-hidden');
        visibleCount += 1;
      } else {
        card.setAttribute('data-blog-filter-hidden', 'true');
      }
    });

    if (this.noResults instanceof HTMLElement) {
      this.noResults.hidden = visibleCount > 0;
    }

    if (this.blogTitle instanceof HTMLElement) {
      if (term) {
        this.blogTitle.textContent = `Search Results for ${rawTerm.trim()}`;
      } else {
        this.blogTitle.textContent = this.defaultTitle;
      }
    }

    const bothPaginations = this.root.querySelectorAll('.amd-pagination');
    bothPaginations.forEach((paginationNode) => {
      if (!(paginationNode instanceof HTMLElement)) {
        return;
      }
      paginationNode.style.display = term ? 'none' : '';
    });
  }

  async ensureAllPagesLoaded() {
    if (!(this.cardsContainer instanceof HTMLElement)) {
      return;
    }

    if (this.isLoadingPages) {
      return;
    }

    const pageUrls = this.getPaginationUrls();
    if (!pageUrls.length) {
      return;
    }

    this.isLoadingPages = true;
    this.showLoadingMessage();

    for (const pageUrl of pageUrls) {
      if (this.cacheLoadedUrls.has(pageUrl)) {
        continue;
      }

      let html = '';
      try {
        const pageResp = await fetch(pageUrl, { credentials: 'same-origin' });
        if (!pageResp.ok) {
          continue;
        }
        html = await pageResp.text();
      } catch (error) {
        continue;
      }

      if (!html) {
        continue;
      }

      this.appendCardsFromHtml(html);
      this.cacheLoadedUrls.add(pageUrl);
    }

    this.hideLoadingMessage();
    this.isLoadingPages = false;
  }

  getPaginationUrls() {
    const links = Array.from(this.root.querySelectorAll('.amd-pagination .page-numbers[href]'));
    const urls = links
      .map((link) => link.getAttribute('href'))
      .filter((href) => typeof href === 'string' && href.trim() !== '')
      .map((href) => {
        try {
          const absolute = new URL(href, window.location.origin);
          return `${absolute.pathname}${absolute.search}`;
        } catch (error) {
          return href;
        }
      })
      .filter((href) => href !== window.location.pathname + window.location.search);

    return [...new Set(urls)];
  }

  appendCardsFromHtml(html) {
    if (!(this.cardsContainer instanceof HTMLElement)) {
      return;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    let sourceCardsContainer = doc.querySelector('[data-blog-cards]');
    if (!(sourceCardsContainer instanceof HTMLElement)) {
      sourceCardsContainer = doc.querySelector('[data-blog-posts-list]');
    }

    if (!(sourceCardsContainer instanceof HTMLElement)) {
      return;
    }

    const incomingCards = Array.from(sourceCardsContainer.querySelectorAll('[data-blog-post]'));
    incomingCards.forEach((card) => {
      const idClass = Array.from(card.classList).find((className) => className.startsWith('single-listing-'));
      if (!idClass) {
        return;
      }

      const duplicate = this.cardsContainer.querySelector(`.${idClass}`);
      if (duplicate) {
        return;
      }

      const cleanCard = document.importNode(card, true);
      cleanCard.removeAttribute('data-aos');
      cleanCard.removeAttribute('data-aos-once');
      this.cardsContainer.appendChild(cleanCard);
      this.postCards.push(cleanCard);
    });
  }

  showLoadingMessage() {
    if (!(this.postsList instanceof HTMLElement)) {
      return;
    }

    if (this.loadingMessage instanceof HTMLElement) {
      this.loadingMessage.hidden = false;
      return;
    }

    const loadingNode = document.createElement('p');
    loadingNode.className = 'amd-blog-loading';
    loadingNode.textContent = 'Loading more posts...';
    this.postsList.appendChild(loadingNode);
    this.loadingMessage = loadingNode;
  }

  hideLoadingMessage() {
    if (this.loadingMessage instanceof HTMLElement) {
      this.loadingMessage.hidden = true;
    }
  }

  resetToFirstPage() {
    const fallbackUrl = window.location.pathname.split('/page/')[0];
    const targetUrl = this.baseBlogUrl || fallbackUrl;
    window.location.href = targetUrl;
  }

  onToggleClick(event) {
    const trigger = event.target.closest('[data-archive-toggle]');
    if (!(trigger instanceof HTMLElement)) {
      return;
    }

    const item = trigger.closest('.amd-widget-archives-list-year, .amd-widget-archives-list-month');
    if (!(item instanceof HTMLElement)) {
      return;
    }

    const targetList = item.querySelector(':scope > .amd-widget-archives-list-months, :scope > .amd-widget-archives-list-posts');
    if (!(targetList instanceof HTMLElement)) {
      return;
    }

    const isOpen = item.classList.contains('is-open');
    if (isOpen) {
      this.collapse(item, targetList, trigger);
      return;
    }

    this.deactivateSiblings(item);
    this.expand(item, targetList, trigger);
  }

  expand(item, list, trigger) {
    item.classList.add('is-open');
    trigger.classList.add('active');
    trigger.setAttribute('aria-expanded', 'true');

    list.style.height = '0px';
    const targetHeight = list.scrollHeight;

    window.requestAnimationFrame(() => {
      list.style.height = `${targetHeight}px`;
    });

    window.setTimeout(() => {
      if (item.classList.contains('is-open')) {
        list.style.height = 'auto';
      }
    }, 260);
  }

  collapse(item, list, trigger) {
    const startHeight = list.scrollHeight;
    list.style.height = `${startHeight}px`;

    window.requestAnimationFrame(() => {
      list.style.height = '0px';
    });

    item.classList.remove('is-open');
    trigger.classList.remove('active');
    trigger.setAttribute('aria-expanded', 'false');

    const nestedOpen = item.querySelectorAll('.is-open');
    nestedOpen.forEach((node) => {
      node.classList.remove('is-open');

      const nestedButton = node.querySelector(':scope > [data-archive-toggle]');
      if (nestedButton instanceof HTMLElement) {
        nestedButton.classList.remove('active');
        nestedButton.setAttribute('aria-expanded', 'false');
      }

      const nestedList = node.querySelector(':scope > .amd-widget-archives-list-months, :scope > .amd-widget-archives-list-posts');
      if (nestedList instanceof HTMLElement) {
        nestedList.style.height = '0px';
      }
    });
  }

  deactivateSiblings(item) {
    const parentList = item.parentElement;
    if (!(parentList instanceof HTMLElement)) {
      return;
    }

    const siblings = Array.from(parentList.children).filter((node) => node !== item);
    siblings.forEach((sibling) => {
      if (!(sibling instanceof HTMLElement)) {
        return;
      }

      if (!sibling.classList.contains('is-open')) {
        return;
      }

      const siblingTrigger = sibling.querySelector(':scope > [data-archive-toggle]');
      const siblingList = sibling.querySelector(':scope > .amd-widget-archives-list-months, :scope > .amd-widget-archives-list-posts');
      if (!(siblingTrigger instanceof HTMLElement) || !(siblingList instanceof HTMLElement)) {
        return;
      }

      this.collapse(sibling, siblingList, siblingTrigger);
    });
  }
}

const blogInstances = new Map();

function initMainBlog(scope) {
  const root = scope instanceof HTMLElement || scope instanceof Document ? scope : document;
  const nodes = root.querySelectorAll('[data-amd-blog-listing]');

  nodes.forEach((node) => {
    if (blogInstances.has(node)) {
      return;
    }

    const instance = new MainBlogArchives(node);
    instance.init();
    blogInstances.set(node, instance);
  });
}

function destroyMainBlog(scope) {
  if (!(scope instanceof HTMLElement)) {
    return;
  }

  const nodes = scope.querySelectorAll('[data-amd-blog-listing]');
  nodes.forEach((node) => {
    const instance = blogInstances.get(node);
    if (!instance) {
      return;
    }

    instance.destroy();
    blogInstances.delete(node);
  });

  if (scope.matches('[data-amd-blog-listing]')) {
    const instance = blogInstances.get(scope);
    if (instance) {
      instance.destroy();
      blogInstances.delete(scope);
    }
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initMainBlog(document);
  });
} else {
  initMainBlog(document);
}

document.addEventListener('shopify:section:load', (event) => {
  initMainBlog(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  destroyMainBlog(event.target);
});
